import type { Event } from '@buzzmap/shared';
import { BuzzPin } from '../icons/BuzzPin';
import { parseCalendarDate } from '../../utils/dates';
import styles from './EventMeta.module.css';

interface EventMetaProps {
  event: Event;
}

/**
 * Format date to Italian locale display string.
 */
function formatDate(dateString: string): string {
  const date = parseCalendarDate(dateString);
  return date.toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Format date range for display.
 */
function formatDateRange(startDate: string, endDate?: string): string {
  if (!endDate || startDate === endDate) {
    return formatDate(startDate);
  }

  const start = parseCalendarDate(startDate);
  const end = parseCalendarDate(endDate);

  // Same month and year
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `dal ${start.getDate()} al ${end.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })}`;
  }

  // Same year
  if (start.getFullYear() === end.getFullYear()) {
    return `dal ${start.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
    })} al ${end.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })}`;
  }

  return `dal ${formatDate(startDate)} al ${formatDate(endDate)}`;
}

/**
 * Format time for display (HH:MM format).
 */
function formatTime(time?: string): string | null {
  if (!time) return null;
  return time.slice(0, 5); // Remove seconds if present
}

/**
 * Format time range for display.
 */
function formatTimeRange(startTime?: string, endTime?: string): string | null {
  const start = formatTime(startTime);
  if (!start) return null;

  const end = formatTime(endTime);
  if (end) {
    return `dalle ${start} alle ${end}`;
  }

  return `dalle ore ${start}`;
}

/**
 * Build full address string from location.
 */
function buildAddress(location: Event['location']): string {
  const parts: string[] = [];

  if (location.venueName) {
    parts.push(location.venueName);
  }

  if (location.address) {
    parts.push(location.address);
  }

  if (location.postalCode) {
    parts.push(location.postalCode);
  }

  if (location.city) {
    let cityText = location.city;
    if (location.province) {
      cityText += ` (${location.province})`;
    }
    parts.push(cityText);
  }

  return parts.join(', ') || location.originalText || 'Indirizzo non disponibile';
}

/**
 * Open directions in Google Maps.
 */
function openDirections(location: Event['location']) {
  const { coordinates, address, city, venueName } = location;

  let destination: string;
  if (coordinates) {
    destination = `${coordinates.latitude},${coordinates.longitude}`;
  } else {
    const parts = [venueName, address, city].filter(Boolean);
    destination = encodeURIComponent(parts.join(', '));
  }

  window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}`, '_blank');
}

/**
 * EventMeta component displays event metadata sections.
 */
export function EventMeta({ event }: EventMetaProps) {
  const dateDisplay = formatDateRange(event.startDate, event.endDate);
  const timeDisplay = formatTimeRange(event.startTime, event.endTime);
  const addressDisplay = buildAddress(event.location);

  return (
    <div className={styles.container}>
      {/* DESCRIZIONE - Description (above date/location cards) */}
      {event.description && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>📝</span>
            Descrizione
          </h2>
          <div className={styles.sectionContent}>
            <p className={styles.descriptionText}>{event.description}</p>
          </div>
        </section>
      )}

      {/* QUANDO - Date and Time */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <span className={styles.sectionIcon}>📅</span>
          Quando
        </h2>
        <div className={styles.sectionContent}>
          <p className={styles.dateText}>{dateDisplay}</p>
          {timeDisplay && <p className={styles.timeText}>{timeDisplay}</p>}
          {event.dateOriginalText && event.dateOriginalText !== dateDisplay && (
            <p className={styles.originalText}>{event.dateOriginalText}</p>
          )}
        </div>
      </section>

      {/* DOVE - Location */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <BuzzPin size={18} className={styles.sectionIcon} />
          Dove
        </h2>
        <div className={styles.sectionContent}>
          <p className={styles.addressText}>{addressDisplay}</p>
          <button
            className={styles.directionsButton}
            onClick={() => openDirections(event.location)}
            aria-label="Apri indicazioni stradali"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="3 11 22 2 13 21 11 13 3 11" />
            </svg>
            Indicazioni
          </button>
        </div>
      </section>

      {/* SOURCE - Link to original source */}
      {event.sourceUrl && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>🔗</span>
            Fonte
          </h2>
          <div className={styles.sectionContent}>
            <a
              href={event.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.sourceLink}
            >
              Vai alla pagina originale
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          </div>
        </section>
      )}
    </div>
  );
}
